import React, { useEffect, useState } from 'react';
import { Alert, Form, Input, Modal, Select, message } from 'antd';
import { clearVisitArea, setVisitScope } from '../../services/api';
import { getApiError } from '../../utils/apiError';
import { Area } from './checklistTypes';

const REASONS = [
  { value: 'NOT_IN_SCOPE', label: 'Not in audit scope' },
  { value: 'OTHER_AUDIT', label: 'Covered by another audit / auditors' },
  { value: 'NOT_UNDERWAY', label: 'Not currently underway' },
  { value: 'NOT_STARTED', label: 'Yet to start' },
  { value: 'OTHER', label: 'Other' },
];

interface Props {
  visitId: string;
  area: Area | null;
  answeredCount: number;
  reviewer?: boolean;
  reopen?: boolean;
  onClose: () => void;
  onDone: () => void;
}

const ScopeModal: React.FC<Props> = ({ visitId, area, answeredCount, reviewer, reopen, onClose, onDone }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const reason = Form.useWatch('reason', form);

  useEffect(() => { if (area) form.resetFields(); }, [area, form]);

  const save = async () => {
    if (!area) return;
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (!reopen && !reviewer && answeredCount > 0) await clearVisitArea(visitId, area.id);
      await setVisitScope(visitId, {
        item_id: area.id, not_applicable: !reopen, reason: values.reason || '', remarks: values.remarks || '',
        reviewer_reason: values.reviewer_reason || '',
      });
      message.success(reopen ? `${area.code} reopened.` : `${area.code} closed.`);
      onDone();
    } catch (err) {
      message.error(getApiError(err, 'Could not close this area.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={area ? `${reopen ? 'Reopen' : 'Close'} ${area.code}${reopen ? '' : ' (not audited)'}` : ''}
      open={!!area}
      onCancel={onClose}
      onOk={save}
      okText={reopen ? 'Reopen area' : answeredCount > 0 && !reviewer ? 'Clear answers and close' : 'Close area'}
      okButtonProps={{ danger: answeredCount > 0 && !reopen, disabled: !reopen && !!reviewer && answeredCount > 0 }}
      confirmLoading={saving}
      destroyOnHidden
    >
      {!reopen && (
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }}
          message="Everything below this area is shut: no audit work is done there and it is left out of the rating."
        />
      )}
      {!reopen && reviewer && answeredCount > 0 && (
        <Alert
          type="error" showIcon style={{ marginBottom: 16 }}
          message={`${answeredCount} answer(s) exist here. Only the lead auditor can clear them, so return the visit to the auditors instead.`}
        />
      )}
      {!reopen && !reviewer && answeredCount > 0 && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 16 }}
          message={`${answeredCount} answer(s) already exist here. Closing will clear them and withdraw their draft NCs.`}
        />
      )}
      <Form form={form} layout="vertical">
        {!reopen && (
          <>
            <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Choose a reason' }]}>
              <Select options={REASONS} />
            </Form.Item>
            <Form.Item
              name="remarks" label={reason === 'OTHER' ? 'Write the reason' : 'Remarks (optional)'}
              rules={reason === 'OTHER' ? [{ required: true, message: 'Please write the reason' }] : []}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </>
        )}
        {reviewer && (
          <Form.Item
            name="reviewer_reason" label="Reason for this change (listed in the report)"
            rules={[{ required: true, message: 'A written reason is required' }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default ScopeModal;
